import {
  ClientSecret,
  deleteIrisQueue,
  deleteIrisSecret,
  IrisSummary,
  updateQueueFilters,
} from "api/account/client";
import Button from "components/components/Button/Button";
import InfoBox from "components/components/InfoBox/InfoBox";
import ClientDetails from "components/shared/ClientDetails/ClientDetails";
import SecretsTable from "components/shared/SecretsTable/SecretsTable";
import { RequestStatus } from "hooks/useRequest";
import React, { useMemo, useState } from "react";

import CreateQueueModal from "./CreateQueueModal/CreateQueueModal";
import IrisDeleteModal from "./IrisDeleteModal/IrisDeleteModal";
import MaximumQueueReachedModal from "./MaximumQueueReachedModal/MaximumQueueReachedModal";
import QueueInfoCard from "./QueueInfoCard/QueueInfoCard";
import {
  ClientDetailsContainer,
  ContentDiv,
  CreateQueueContainer,
  InfoBoxContainer,
  RequestSecretButton,
} from "./style";

const EXPIRED_SECRET_TOOLTIP =
  "Request a new secret and replace it in your client to resume receiving IRIS messages.";
const EXPIRING_SOON_SECRET_TOOLTIP =
  "Request a new secret before this secret expires and replace it " +
  "in your client to ensure you continue receiving IRIS messages.";

type Props = {
  data: IrisSummary;
  fetchIrisDetails: () => void;
  fetchIrisDetailsStatus: RequestStatus;
  requestNewSecret: () => void;
};

const IrisDetails: React.FC<Props> = ({
  data,
  fetchIrisDetails,
  fetchIrisDetailsStatus,
  requestNewSecret,
}) => {
  const [secretToDelete, setSecretToDelete] = useState<ClientSecret | null>(
    null
  );
  const [isCreateQueueModalOpen, setIsCreateQueueModalOpen] =
    useState<boolean>(false);
  const [isMaximumQueueModalOpen, setIsMaximumQueueModalOpen] =
    useState<boolean>(false);
  const [queueToDeleteFiltersFor, setQueueToDeleteFiltersFor] = useState<
    string | null
  >(null);
  const [queueToDelete, setQueueToDelete] = useState<string | null>(null);

  const buttonsDisabled = useMemo(
    () => fetchIrisDetailsStatus === RequestStatus.IN_PROGRESS,
    [fetchIrisDetailsStatus]
  );

  const noSecrets = data.applicationSecrets.length === 0;

  const hasQueues = data.queues.length > 0;

  const maximumQueues = data.maxQueueCount;
  const queuesRemaining = maximumQueues - data.queues.length;

  return (
    <>
      <ClientDetailsContainer>
        <ClientDetails
          items={[
            { label: "Client ID", value: data.appClientId },
            { label: "Service Bus namespace", value: data.serviceBusNamespace },
            {
              label: "Tenant ID",
              value: process.env.REACT_APP_AUTH_TENANT_ID!,
            },
          ]}
        />
        <InfoBoxContainer>
          {hasQueues ? (
            <InfoBox
              title="Inactive queues are deleted after 60 days"
              description="A new queue can always be created when needed using this page."
            />
          ) : (
            <InfoBox
              title="You don't have any queues yet"
              description="Create a queue to receive IRIS messages. Queues are deleted after 60 days of inactivity."
            />
          )}
        </InfoBoxContainer>
        <br />
        {hasQueues &&
          data.queues.map((queue, index) => (
            <QueueInfoCard
              key={index}
              queueName={queue.queueName}
              queueTitle={queue.queueTitle}
              queueUrl={queue.queueUrl}
              filters={queue.filters}
              fetchIrisDetails={fetchIrisDetails}
              onDeleteClick={(): void => setQueueToDelete(queue.queueTitle)}
              onRemoveAllFiltersClick={(): void =>
                setQueueToDeleteFiltersFor(queue.queueTitle)
              }
            />
          ))}
        {queueToDeleteFiltersFor && (
          <IrisDeleteModal
            callDeleteEndpoint={(queueTitle): Promise<void> =>
              updateQueueFilters(queueTitle, [])
            }
            close={(): void => setQueueToDeleteFiltersFor(null)}
            description={
              <>
                This will take effect for messages received{" "}
                <strong>after</strong> this change is confirmed.
              </>
            }
            itemId={queueToDeleteFiltersFor}
            itemLabel="filters"
            fetchIrisDetails={fetchIrisDetails}
            title={
              <p>
                Are you sure you want to remove all filters from the queue
                &apos;{queueToDeleteFiltersFor}&apos;?
              </p>
            }
          />
        )}
        {queueToDelete && (
          <IrisDeleteModal
            callDeleteEndpoint={deleteIrisQueue}
            close={(): void => setQueueToDelete(null)}
            description={
              <>
                Once you delete a queue,{" "}
                <strong>you will not be able to retrieve it</strong> and any
                unread messages on the queue will be lost.
              </>
            }
            itemId={queueToDelete}
            itemLabel="queue"
            fetchIrisDetails={fetchIrisDetails}
            title={
              <p>
                Are you sure you want to delete <br /> &apos;{queueToDelete}
                &apos;?
              </p>
            }
          />
        )}
        <CreateQueueContainer>
          <Button
            buttonText="Create Queue"
            onClick={(): void =>
              queuesRemaining === 0
                ? setIsMaximumQueueModalOpen(true)
                : setIsCreateQueueModalOpen(true)
            }
          />
          <p>
            <b>{queuesRemaining} queues remaining</b> (max. {maximumQueues})
          </p>
        </CreateQueueContainer>
        <CreateQueueModal
          close={(): void => setIsCreateQueueModalOpen(false)}
          isActive={isCreateQueueModalOpen}
          fetchIrisDetails={fetchIrisDetails}
          queueTitles={data.queues.map((queue) => queue.queueTitle)}
        />
        <MaximumQueueReachedModal
          isActive={isMaximumQueueModalOpen}
          close={(): void => setIsMaximumQueueModalOpen(false)}
        />
        <SecretsTable
          applicationSecrets={data.applicationSecrets}
          deleteDisabled={buttonsDisabled}
          setSecretToDelete={setSecretToDelete}
          expiredTooltipText={EXPIRED_SECRET_TOOLTIP}
          expiringSoonTooltipText={EXPIRING_SOON_SECRET_TOOLTIP}
        />
        <ContentDiv aria-live="polite">
          {noSecrets ? (
            <p>
              There are no secrets associated with your Client ID. To receive
              IRIS messages, please{" "}
              <RequestSecretButton
                type="button"
                onClick={requestNewSecret}
                disabled={buttonsDisabled}
                className="is-link"
              >
                <b>request a secret</b>
              </RequestSecretButton>
              .
            </p>
          ) : (
            <p>
              You can request a new secret at any time and each secret is valid
              for 2 years. Once generated, a secret cannot be retrieved. If you
              have lost your secret or require a new one, please{" "}
              <RequestSecretButton
                type="button"
                onClick={requestNewSecret}
                disabled={buttonsDisabled}
                className="is-link"
              >
                <b>request a new secret</b>
              </RequestSecretButton>
              .
            </p>
          )}
        </ContentDiv>
      </ClientDetailsContainer>
      {secretToDelete && (
        <IrisDeleteModal
          callDeleteEndpoint={deleteIrisSecret}
          close={(): void => setSecretToDelete(null)}
          description={
            <p>
              Please ensure secret{" "}
              <b>{secretToDelete.secretHint.padEnd(18, "*")}</b> is not one you
              are currently using in your client, otherwise you will stop
              receiving IRIS messages.
            </p>
          }
          fetchIrisDetails={fetchIrisDetails}
          itemId={secretToDelete.secretId}
          itemLabel="secret"
          title="Are you sure you want to delete this secret?"
        />
      )}
    </>
  );
};

export default IrisDetails;
